import { type Dispatch, type SetStateAction } from "react";

import { Box, Theme, Typography } from "@mui/material";
import { type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import { t } from "i18next";
import { formatNum, getDifferenceForRow } from "src/lib/number.helpers";
import { type MeasuresSummarizedItemDto } from "src/types/dashboardDatasetDto";

import TogglePinned from "../Dashboard/TooglePinned/TogglePinned";
import Difference from "../Difference/Difference";
import DifferenceChip from "../Difference/DifferenceChip";
import PinnedItemMenu from "../RegionDashboard/Pinned/PinnedItemMenu";

const getStyles = (theme: Theme) => ({
  colorBox: (color = theme.palette.transparent.pure) => ({
    backgroundColor: color,
    width: 5,
    height: 20,
    borderRadius: theme.radius.sm,
    mr: 1,
  }),
  tableHeader: {
    fontWeight: theme.typography.fontWeightLight,
    fontSize: theme.fontSizes.sm,
    verticalAlign: "middle",
  },
  name: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.fontSizes.md,
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  currentValue: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.fontSizes.lg,
    width: "15%",
  },
  pastValue: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.fontSizes.lg,
    width: "15%",
    color: theme.palette.text.secondary,
  },
  cellBox: {
    display: "flex",
    alignItems: "center",
    height: "100%",
    width: "100%",
    overflow: "hidden",
  },
  actionsCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "end",
    height: "100%",
  },
});

function createColumns(showColorColumn: boolean, theme: Theme, setError: Dispatch<SetStateAction<string | null>>): GridColDef[] {
  const styles = getStyles(theme);
  return [
    {
      field: "localName",
      headerName: t("Name"),
      renderHeader: () => (
        <Typography sx={styles.tableHeader} component='div'>
          {t("name")}
        </Typography>
      ),
      flex: 10,
      minWidth: 200,
      headerAlign: "left",
      align: "left",
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={styles.cellBox}>
          {showColorColumn && <Box sx={styles.colorBox(params.row.color)} />}
          <Typography sx={styles.name} component='div'>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "currentValue",
      headerName: t("Current period"),
      renderHeader: () => (
        <Typography sx={styles.tableHeader} component='div'>
          {t("current period")}
        </Typography>
      ),
      minWidth: 130,
      flex: 4,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueGetter: (value) => (value as number) ?? 0,
      valueFormatter: (value) => formatNum(value as number),
      renderCell: (params: GridRenderCellParams) => (
        <Box display='flex' alignItems='center' height='100%'>
          <Typography sx={styles.currentValue} component='div'>
            {params.formattedValue}
          </Typography>
        </Box>
      ),
    },
    {
      field: "pastValue",
      headerName: t("Last period"),
      renderHeader: () => <Typography sx={styles.tableHeader}>{t("last period")}</Typography>,
      minWidth: 130,
      flex: 4,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueGetter: (value) => (value as number) ?? 0,
      valueFormatter: (value) => formatNum(value as number),
      renderCell: (params: GridRenderCellParams) => (
        <Box display='flex' alignItems='center' height='100%'>
          <Typography sx={styles.pastValue}> {params.formattedValue}</Typography>
        </Box>
      ),
    },
    {
      field: "difference",
      headerName: t("Yearly change"),
      renderHeader: () => <Typography sx={styles.tableHeader}>{t("yearly change")}</Typography>,
      minWidth: 130,
      flex: 4,
      valueGetter: (row: MeasuresSummarizedItemDto) => (row.currentValue ?? 0) - (row.pastValue ?? 0),
      renderCell: (params: GridRenderCellParams) => <Difference row={params.row} fontSize={theme.fontSizes.lg} />,
    },
    {
      field: "differenceChip",
      headerName: "",
      align: "right",
      minWidth: 130,
      flex: 4,
      valueGetter: (row: MeasuresSummarizedItemDto) => getDifferenceForRow(row),
      renderCell: (params: GridRenderCellParams<MeasuresSummarizedItemDto>) => (
        <DifferenceChip difference={params.value} fontSize={theme.fontSizes.lg} />
      ),
    },
    {
      field: "actions",
      headerName: "",
      align: "right",
      minWidth: 100,
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={styles.actionsCell}>
          <TogglePinned
            fontSize={theme.fontSizes.xl}
            locationId={params.row.id}
            type={params.row.differenceChip.type}
            setError={setError}
          />
          <PinnedItemMenu locationId={params.row.id} type={params.row.differenceChip.type} pin setError={setError} />
        </Box>
      ),
    },
  ];
}

export default createColumns;
